import { ApiResponse, DocumentWorkflowTask } from '../types'
import { apiService } from './api.service'

export interface DocumentWorkflowTaskListParams {
  page?: number
  page_size?: number
  status?: 'open' | 'resolved' | 'cancelled'
  workflow_definition?: number
  document_title?: string
  /** When true, API returns grouped rows; count = number of groups. */
  grouped?: boolean
  /** When grouped=1, use group_by=doc_wf to get one row per (document, workflow) with open_tasks and resolved_tasks. */
  group_by?: 'doc_wf'
}

/** One row per (document, workflow, step) when using grouped=1 without group_by=doc_wf */
export interface GroupedWorkflowTaskRow {
  document_id: number
  document_title: string
  workflow_definition_id: number
  workflow_definition_name: string
  step_id: number
  step_name: string
  status: 'open' | 'resolved' | 'cancelled'
  assigned_display?: string
  assigned_usernames: string[]
  created_at: string
}

/** Resolved task with audit data (action, comment) for subtable */
export interface ResolvedTaskDetail {
  step_id: number
  step_name: string
  status: 'open' | 'resolved' | 'cancelled'
  assigned_display?: string
  assigned_usernames: string[]
  created_at: string
  resolved_at: string | null
  resolved_by_username: string
  comment: string
  action_name: string
}

/** Open task summary for a step */
export interface OpenTaskSummary {
  step_id: number
  step_name: string
  assigned_display?: string
  assigned_usernames: string[]
  created_at: string
}

/** One row per (document, workflow) when using grouped=1&group_by=doc_wf */
export interface GroupedByDocWfRow {
  document_id: number
  document_title: string
  workflow_definition_id: number
  workflow_definition_name: string
  workflow_status_name: string
  overall_status: 'open' | 'resolved' | 'cancelled'
  open_tasks: OpenTaskSummary[]
  resolved_tasks: ResolvedTaskDetail[]
}

export const documentWorkflowTaskService = {
  async getTasks(params?: DocumentWorkflowTaskListParams): Promise<ApiResponse<DocumentWorkflowTask>> {
    const clean: Record<string, string | number | boolean | undefined> = {}
    if (params?.page != null) clean.page = params.page
    if (params?.page_size != null) clean.page_size = params.page_size
    if (params?.status) clean.status = params.status
    if (params?.workflow_definition != null) clean.workflow_definition = params.workflow_definition
    if (params?.document_title?.trim()) clean.document_title = params.document_title.trim()
    const response = await apiService.get<ApiResponse<DocumentWorkflowTask>>('document_workflow_tasks/', { params: clean })
    return response.data
  },

  async getGroupedTasks(params?: DocumentWorkflowTaskListParams): Promise<ApiResponse<GroupedWorkflowTaskRow>> {
    const clean: Record<string, string | number | boolean | undefined> = {}
    if (params?.page != null) clean.page = params.page
    if (params?.page_size != null) clean.page_size = params.page_size
    if (params?.status) clean.status = params.status
    if (params?.workflow_definition != null) clean.workflow_definition = params.workflow_definition
    if (params?.document_title?.trim()) clean.document_title = params.document_title.trim()
    clean.grouped = 1
    const response = await apiService.get<ApiResponse<GroupedWorkflowTaskRow>>('document_workflow_tasks/', { params: clean })
    return response.data
  },

  async getGroupedTasksByDocWf(params?: DocumentWorkflowTaskListParams): Promise<ApiResponse<GroupedByDocWfRow>> {
    const clean: Record<string, string | number | boolean | undefined> = {}
    if (params?.page != null) clean.page = params.page
    if (params?.page_size != null) clean.page_size = params.page_size
    if (params?.status) clean.status = params.status
    if (params?.workflow_definition != null) clean.workflow_definition = params.workflow_definition
    if (params?.document_title?.trim()) clean.document_title = params.document_title.trim()
    clean.grouped = 1
    clean.group_by = 'doc_wf'
    const response = await apiService.get<ApiResponse<GroupedByDocWfRow>>('document_workflow_tasks/', { params: clean })
    return response.data
  },

  async getTask(id: number): Promise<DocumentWorkflowTask> {
    const response = await apiService.get<DocumentWorkflowTask>(`document_workflow_tasks/${id}/`)
    return response.data
  },

  async getWorkflowDefinitionsForFilter(): Promise<{ results: { id: number; name: string }[] }> {
    const response = await apiService.get<{ results: { id: number; name: string }[] }>(
      'document_workflow_tasks/workflow_definitions/'
    )
    return response.data
  },
}
